import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import { Close } from '@mui/icons-material';

import useInstallPwa, {
  isMobileDevice,
  isInApp,
  isIOS,
  needsChrome,
  escapeWebView,
} from '../../hooks/useInstallPwa';

const INSTALL_PAGE = '/installer';
const EXCLUDED_PATHS = [INSTALL_PAGE, '/profile'];

const InstallGate = ({ children }) => {
  const [show, setShow] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const {
    triggerInstall, isAppInstalled,
  } = useInstallPwa();

  useEffect(() => {
    if (isMobileDevice && !isInApp && !isAppInstalled) {
      setShow(true);
    }
  }, [isAppInstalled]);

  useEffect(() => {
    if (isAppInstalled) {
      setShow(false);
    }
  }, [isAppInstalled]);

  // Si le prompt natif est indisponible (Chrome ne l'émet qu'au chargement
  // de page et il a pu être manqué, ou navigateur in-app...), on redirige
  // vers la page d'installation en rechargement complet : Chrome ré-émet
  // alors l'événement, capté au plus tôt par le listener de index.html.
  const handleOpenInstall = async () => {
    if (isIOS) {
      navigate(INSTALL_PAGE);
      return;
    }
    if (needsChrome) {
      // WebView in-app : le prompt n'y est jamais émis. On tente de
      // rouvrir directement dans Chrome, sinon on retombe sur la page
      // d'installation et ses instructions.
      escapeWebView(
        INSTALL_PAGE,
        () => window.location.assign(INSTALL_PAGE)
      );
      return;
    }
    const accepted = await triggerInstall();
    if (accepted) {
      setShow(false);
    } else if (accepted === null) {
      window.location.assign(INSTALL_PAGE);
    }
  };

  const handleDismiss = () => setShow(false);

  return (
    <>
      {children || null}

      {show && !EXCLUDED_PATHS.includes(location.pathname) && (
        <Paper
          elevation={8}
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 1299,
            px: 2,
            pt: 1.5,
            pb: 2,
            borderRadius: 0,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
            <Box
              component="img"
              src="/icon-192.png"
              alt="Les Bons Artisans"
              onError={(e) => { e.target.style.display = 'none'; }}
              sx={{ width: 40, height: 40, borderRadius: 1.5, flexShrink: 0 }}
            />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" fontWeight="bold" noWrap>
                Utilisez l&apos;application
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                Profitez d&apos;une expérience plus fluide
              </Typography>
            </Box>
            <IconButton
              size="small"
              onClick={handleDismiss}
              sx={{
                flexShrink: 0,
                color: 'text.secondary',
                alignSelf: 'flex-start',
              }}
            >
              <Close fontSize="small" />
            </IconButton>
          </Box>
          <Button
            variant="contained"
            fullWidth
            onClick={handleOpenInstall}
            sx={{ fontWeight: 'bold' }}
          >
            Installer
          </Button>
        </Paper>
      )}
    </>
  );
};

export default InstallGate;
